import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import { Menu, MenuItem } from 'carbon-react/lib/components/menu';
import NavigationBar from 'carbon-react/lib/components/navigation-bar';

import styles from './secondary-navbar.scss';

export interface SecondaryNavbarMenuItem {
  id: string;
  name: string;
  url: string;
  submenu?: SecondaryNavbarMenuItem[];
}

export interface SecondaryNavbarRightMenuItem {
  name: string;
  url?: string;
  children?: { name: string; url: string }[];
}

interface SecondaryNavbarProps {
  leftMenuItems?: SecondaryNavbarMenuItem[];
  marginTop?: boolean;
  rightMenuItems?: SecondaryNavbarRightMenuItem[];
  rightExtraContent?: ReactNode;
}

export const SecondaryNavbar = ({
  leftMenuItems = [],
  marginTop = false,
  rightMenuItems = [],
  rightExtraContent
}: SecondaryNavbarProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>, url: string) => {
    if (!url) {
      return;
    }

    const isExternalLink = new URL(url, window.location.href).origin !== window.location.origin;

    // Allow native anchor behavior for external links and modified clicks (cmd+click, ctrl+click, middle-click)
    if (isExternalLink || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      return;
    }

    // Prevent default and use client-side navigation for normal clicks
    e.preventDefault();
    navigate(url);
  };

  return (
    <div
      className={clsx(styles.secondaryNavbar, {
        [styles.marginTop]: marginTop
      })}
    >
      <NavigationBar variant="white" orientation="top" ariaLabel="Secondary navigation bar" padding="0 24px">
        <Box display="flex" justifyContent="space-between" alignItems="stretch" width="100%">
          <Menu menuType="white" flex="1">
            {leftMenuItems.map(item => {
              const isActive = !!item.url && pathname.startsWith(item.url);
              return item.submenu ? (
                <MenuItem key={item.id} submenu={item.name} selected={isActive}>
                  {item.submenu.map(subItem => (
                    <MenuItem
                      key={subItem.id}
                      href={subItem.url}
                      onClick={e => handleClick(e, subItem.url)}
                      selected={isActive}
                    >
                      {subItem.name}
                    </MenuItem>
                  ))}
                </MenuItem>
              ) : (
                <MenuItem key={item.id} href={item.url} onClick={e => handleClick(e, item.url)} selected={isActive}>
                  {item.name}
                </MenuItem>
              );
            })}
          </Menu>

          <Box display="flex" alignItems="stretch" gap="8px">
            {rightMenuItems.length > 0 && (
              <Menu menuType="white">
                {rightMenuItems.map(item =>
                  item.url ? (
                    <MenuItem key={item.name} href={item.url} onClick={e => handleClick(e, item.url!)}>
                      {item.name}
                    </MenuItem>
                  ) : (
                    <MenuItem key={item.name} submenu={item.name} submenuDirection="left">
                      {item.children?.map(child => (
                        <MenuItem key={child.url} href={child.url} onClick={e => handleClick(e, child.url)}>
                          {child.name}
                        </MenuItem>
                      ))}
                    </MenuItem>
                  )
                )}
              </Menu>
            )}
            {rightExtraContent}
          </Box>
        </Box>
      </NavigationBar>
    </div>
  );
};
